import type { ReactNode } from "react";
import { CheckCircle2, AlertTriangle, XCircle, CircleDot, Info } from "lucide-react";
import { cn } from "../../lib/cn";

export type Tone = "neutral" | "accent" | "good" | "warning" | "serious" | "critical" | "info";
const tones: Record<Tone, string> = {
  neutral: "bg-panel-2 text-fg-2 border-border",
  accent: "bg-accent-soft text-accent border-accent/30",
  good: "bg-good/10 text-[#4cd14c] border-good/30",
  warning: "bg-warning/10 text-warning border-warning/30",
  serious: "bg-serious/10 text-serious border-serious/30",
  critical: "bg-critical/10 text-[#ff8080] border-critical/40",
  info: "bg-info/10 text-[#7fb2f0] border-info/30",
};

export function Badge({ tone = "neutral", children, className, icon }: { tone?: Tone; children: ReactNode; className?: string; icon?: ReactNode }) {
  return (
    <span className={cn("inline-flex items-center gap-1 whitespace-nowrap rounded border px-1.5 py-0.5 text-[11px] font-medium leading-4", tones[tone], className)}>
      {icon}
      {children}
    </span>
  );
}

const statusColor: Record<Exclude<Tone, "accent" | "neutral" | "info">, string> = {
  good: "var(--good)",
  warning: "var(--warning)",
  serious: "var(--serious)",
  critical: "var(--critical)",
};

/** Status is never color-alone: dot + text label (and icon variant via StatusBadge). */
export function StatusDot({ status, label, pulse }: { status: "good" | "warning" | "serious" | "critical" | "neutral"; label: string; pulse?: boolean }) {
  const color = status === "neutral" ? "var(--text-3)" : statusColor[status];
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-fg-2">
      <span className="relative inline-flex size-2" aria-hidden>
        {pulse && <span className="absolute inset-0 animate-ping rounded-full opacity-60" style={{ background: color }} />}
        <span className="relative inline-flex size-2 rounded-full" style={{ background: color }} />
      </span>
      <span>{label}</span>
    </span>
  );
}

export function StatusBadge({ status, children }: { status: "good" | "warning" | "serious" | "critical" | "neutral" | "info"; children: ReactNode }) {
  const Icon = { good: CheckCircle2, warning: AlertTriangle, serious: AlertTriangle, critical: XCircle, neutral: CircleDot, info: Info }[status];
  return (
    <Badge tone={status} icon={<Icon className="size-3" aria-hidden />}>
      {children}
    </Badge>
  );
}

/** Map lifecycle strings from the API to status tones. */
export function lifecycleTone(s: string | null | undefined): "good" | "warning" | "serious" | "critical" | "neutral" | "info" {
  switch (s) {
    case "active":
    case "online":
    case "done":
    case "ok":
    case "approved":
      return "good";
    case "syncing":
    case "pending":
    case "queued":
    case "leased":
    case "pending_approval":
    case "snapshotting":
    case "streaming":
      return "info";
    case "stale":
    case "paused":
    case "draft":
      return "warning";
    case "offline":
      return "serious";
    case "error":
    case "failed":
    case "revoked":
    case "rejected":
      return "critical";
    default:
      return "neutral";
  }
}
